import React, {useState, useEffect} from "react";
import {useFormik} from "formik";
import {Input, DatePicker, Tag, message, Select} from "antd";
import {EditOutlined, PlusOutlined} from "@ant-design/icons";
import moment from "moment";
import locale from "antd/es/date-picker/locale/tr_TR";
import {useStore} from "hooks/useStore";
import {PoolFeeTypeCard} from "./components/PoolFeeTypeCard";
import {CustomButton} from "components/_defaultComponents/CustomButton/CustomButton";
import {
    FormContainer,
    FormTitle,
    GeneralInfoSection,
    InfoRow,
    InfoLabel,
    FeeTypesContainer,
    ActionButtonsContainer,
    NoteSection
} from "./PoolPaymentContractForm.styles";

// Pool için sadece PAKET ve KM tipleri kullanılabilir
const POOL_FEE_TYPES = [
    {value: "PAKET", name: "Paket"},
    {value: "KM", name: "Mesafe"}
];

const DEFAULT_DAYS_ALL_FALSE = {
    "0": false,
    "1": false,
    "2": false,
    "3": false,
    "4": false,
    "5": false,
    "6": false
};

export const PoolPaymentContractForm = ({activePoolPaymentContract, poolId, onSuccess}) => {
    const stores = useStore();
    const [isEditing, setIsEditing] = useState(false);
    const [feeItemsState, setFeeItemsState] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const userIsCustomer = !stores.meStore.checkRole("notVisibleForCompanyUser");

    useEffect(() => {
        if (isEditing) {
            return;
        }
        if (activePoolPaymentContract?.feeItems) {
            organizeFeeItems(activePoolPaymentContract.feeItems);
        } else {
            initializeEmptyFeeItems();
        }
    }, [JSON.stringify(activePoolPaymentContract), isEditing]);

    const organizeFeeItems = feeItems => {
        const organized = {};

        POOL_FEE_TYPES.forEach(type => {
            const items = feeItems.filter(item => item.feeType === type.value);
            const defaultItem = items.find(item => !item.options?.isCustomTimed);
            const customItems = items.filter(item => item.options?.isCustomTimed);

            if (defaultItem || customItems.length > 0) {
                organized[type.value] = {
                    default: defaultItem || createEmptyFeeItem(type.value),
                    custom: customItems
                };
            }
        });

        setFeeItemsState(organized);
    };

    const initializeEmptyFeeItems = () => {
        setFeeItemsState({});
    };

    const createEmptyFeeItem = feeType => {
        return {
            feeType,
            clientAmount: 0,
            courierAmount: 0,
            courierMinimumDistance: feeType === "KM" ? 0 : null,
            clientMinimumDistance: feeType === "KM" ? 0 : null
        };
    };

    const createCustomFeeItem = feeType => {
        return {
            feeType,
            clientAmount: 0,
            courierAmount: 0,
            courierMinimumDistance: feeType === "KM" ? 0 : null,
            clientMinimumDistance: feeType === "KM" ? 0 : null,
            options: {
                isCustomTimed: true,
                days: DEFAULT_DAYS_ALL_FALSE
            }
        };
    };

    const handleDefaultChange = (feeType, field, value) => {
        setFeeItemsState(prev => {
            const updated = {...prev};
            const fieldPath = field.split(".");

            if (fieldPath.length === 1) {
                updated[feeType].default[field] = value;
            } else {
                if (!updated[feeType].default.options) {
                    updated[feeType].default.options = {};
                }
                updated[feeType].default.options[fieldPath[1]] = value;
            }

            return updated;
        });
    };

    const handleCustomItemChange = (feeType, index, field, value) => {
        setFeeItemsState(prev => {
            const updated = {...prev};
            const fieldPath = field.split(".");

            if (fieldPath.length === 1) {
                updated[feeType].custom[index][field] = value;
            } else {
                if (!updated[feeType].custom[index].options) {
                    updated[feeType].custom[index].options = {};
                }
                updated[feeType].custom[index].options[fieldPath[1]] = value;
            }

            return updated;
        });
    };

    const handleAddCustomItem = feeType => {
        setFeeItemsState(prev => ({
            ...prev,
            [feeType]: {
                ...prev[feeType],
                custom: [...prev[feeType].custom, createCustomFeeItem(feeType)]
            }
        }));
    };

    const handleRemoveCustomItem = (feeType, index) => {
        setFeeItemsState(prev => ({
            ...prev,
            [feeType]: {
                ...prev[feeType],
                custom: prev[feeType].custom.filter((_, i) => i !== index)
            }
        }));
    };

    const handleAddFeeType = feeType => {
        if (!feeType || feeItemsState[feeType]) return;

        setFeeItemsState(prev => ({
            ...prev,
            [feeType]: {
                default: createEmptyFeeItem(feeType),
                custom: []
            }
        }));
    };

    const handleRemoveFeeType = feeType => {
        setFeeItemsState(prev => {
            const updated = {...prev};
            delete updated[feeType];
            return updated;
        });
    };

    const convertFeeItemsToPayload = () => {
        const feeItems = [];

        Object.entries(feeItemsState).forEach(([feeType, items]) => {
            // Add default item if has values
            if (
                items.default?.clientAmount !== undefined ||
                items.default?.courierAmount !== undefined
            ) {
                const defaultItem = {...items.default};

                // Don't send options for PAKET and KM if no custom times
                if (
                    ["PAKET", "KM"].includes(feeType) &&
                    (!defaultItem.options || !defaultItem.options.isCustomTimed)
                ) {
                    delete defaultItem.options;
                }

                feeItems.push(defaultItem);
            }

            // Add custom items
            items.custom?.forEach(item => {
                if (item.clientAmount !== undefined || item.courierAmount !== undefined) {
                    feeItems.push(item);
                }
            });
        });

        return feeItems;
    };

    const validateFeeItems = () => {
        // Özel gün tanımlamalarında en az bir gün seçilmiş olmalı
        for (const [, items] of Object.entries(feeItemsState)) {
            if (items.custom) {
                for (const customItem of items.custom) {
                    if (customItem.options?.isCustomTimed) {
                        const days = customItem.options?.days || {};
                        const hasSelectedDay = Object.values(days).some(
                            val => val === true || val === "true"
                        );

                        if (!hasSelectedDay) {
                            return {
                                isValid: false,
                                message: "Özel gün tanımlamalarında en az bir gün seçilmelidir"
                            };
                        }
                    }
                }
            }
        }
        return {isValid: true};
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const validation = validateFeeItems();
            if (!validation.isValid) {
                message.warning(validation.message);
                return;
            }

            const feeItems = convertFeeItemsToPayload();

            if (feeItems.length === 0) {
                message.warning("En az bir ücret tipi doldurulmalıdır");
                return;
            }

            const contract = {
                poolId: poolId,
                description: formik.values.description || "",
                startDate: formik.values.startDate
                    ? moment(formik.values.startDate).format("YYYY-MM-DD")
                    : moment().format("YYYY-MM-DD"),
                feeItems
            };

            const result = await stores.poolStore.createPoolPaymentContract({contract});

            if (result?.data?.success) {
                message.success("Sözleşme başarıyla kaydedildi");
                setIsEditing(false);
                onSuccess?.();
            } else {
                message.error(result?.data?.message || "Sözleşme kaydedilemedi");
            }
        } catch (error) {
            message.error(error.message || "Bir hata oluştu");
        } finally {
            setIsSaving(false);
        }
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleCancel = () => {
        setIsEditing(false);
        if (activePoolPaymentContract?.feeItems) {
            organizeFeeItems(activePoolPaymentContract.feeItems);
        } else {
            initializeEmptyFeeItems();
        }
        formik.resetForm();
    };

    const formik = useFormik({
        initialValues: {
            description: activePoolPaymentContract?.description || "",
            startDate: activePoolPaymentContract?.startDate
                ? moment(activePoolPaymentContract.startDate)
                : moment()
        }
    });

    return (
        <FormContainer>
            <FormTitle>
                Güncel Havuz Sözleşmesi
                {activePoolPaymentContract?.contractId && (
                    <Tag style={{marginLeft: 12, fontWeight: "normal"}}>
                        {activePoolPaymentContract.contractId}
                    </Tag>
                )}
            </FormTitle>

            <GeneralInfoSection>
                <InfoRow>
                    <InfoLabel>Başlangıç Tarihi:</InfoLabel>
                    <DatePicker
                        className="payment-contract-form-input"
                        locale={locale}
                        value={formik.values.startDate}
                        format="DD.MM.YYYY"
                        disabledDate={current => current && current <= moment().subtract(1, "day")}
                        onChange={date => formik.setFieldValue("startDate", date)}
                        disabled={!isEditing}
                        style={{width: 250}}
                        allowClear={false}
                    />
                </InfoRow>

                <InfoRow>
                    <InfoLabel>Açıklama:</InfoLabel>
                    <Input
                        className="payment-contract-form-input"
                        value={formik.values.description}
                        onChange={e => formik.setFieldValue("description", e.target.value)}
                        disabled={!isEditing}
                        style={{width: "100%", maxWidth: 350}}
                        placeholder="Sözleşme açıklaması"
                    />
                </InfoRow>
            </GeneralInfoSection>

            <FeeTypesContainer>
                {POOL_FEE_TYPES.filter(feeType => feeItemsState[feeType.value]).map(feeType => {
                    return (
                        <PoolFeeTypeCard
                            key={feeType.value}
                            feeType={feeType.value}
                            feeTypeName={feeType.name}
                            defaultFeeItem={feeItemsState[feeType.value]?.default}
                            customFeeItems={feeItemsState[feeType.value]?.custom || []}
                            disabled={!isEditing}
                            onDefaultChange={handleDefaultChange}
                            onCustomItemChange={handleCustomItemChange}
                            onAddCustomItem={handleAddCustomItem}
                            onRemoveCustomItem={handleRemoveCustomItem}
                            onRemoveFeeType={handleRemoveFeeType}
                            userIsCustomer={userIsCustomer}
                        />
                    );
                })}

                {isEditing &&
                    (() => {
                        const availableFeeTypes = POOL_FEE_TYPES.filter(
                            feeType => !feeItemsState[feeType.value]
                        );

                        return availableFeeTypes.length > 0 ? (
                            <div style={{marginTop: 10}}>
                                <Select
                                    className="payment-contract-form-input"
                                    placeholder="Ücret Tipi Ekle"
                                    style={{width: 300}}
                                    onChange={handleAddFeeType}
                                    value={null}
                                    suffixIcon={<PlusOutlined />}
                                >
                                    {availableFeeTypes.map(feeType => (
                                        <Select.Option key={feeType.value} value={feeType.value}>
                                            {feeType.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </div>
                        ) : null;
                    })()}
            </FeeTypesContainer>

            {stores.meStore.checkRole("createSubCompanyPaymentContract") && (
                <ActionButtonsContainer>
                    {!isEditing ? (
                        <CustomButton
                            onClick={handleEdit}
                            icon={<EditOutlined />}
                            style={{minWidth: 120, fontWeight: 600}}
                        >
                            Düzenle
                        </CustomButton>
                    ) : (
                        <>
                            <CustomButton
                                onClick={handleSave}
                                type="primary"
                                style={{minWidth: 120, fontWeight: 600}}
                                loading={isSaving}
                            >
                                Kaydet
                            </CustomButton>
                            <CustomButton
                                onClick={handleCancel}
                                style={{minWidth: 120, fontWeight: 600}}
                                loading={isSaving}
                            >
                                İptal
                            </CustomButton>
                        </>
                    )}
                </ActionButtonsContainer>
            )}
            <NoteSection>
                * Müşteri Ücretleri KDV dahil değildir
                <br />* Kurye Ücretleri KDV dahildir
            </NoteSection>
        </FormContainer>
    );
};
